import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import API from "@src/API.js";
import CustomButton from "@src/components/Controls/CustomButton";
import { useMarketingAuthUi } from "../MarketingAuthUi";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const { openLogin } = useMarketingAuthUi();
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  async function onSubmit(e) {
    e?.preventDefault?.();
    if (busy) return;
    if (!token) {
      setError("Missing reset token.");
      return;
    }
    if (!password || !passwordConfirm) {
      setError("Enter and confirm your new password.");
      return;
    }
    if (password !== passwordConfirm) {
      setError("New password and confirmation do not match.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const resp = await API.postAsync("/PasswordReset/Complete", {
        token,
        password,
        passwordConfirm,
      });
      if (resp?.ok) {
        setDone(true);
        toast.success("Password updated. You can sign in now.");
      } else {
        setError(resp?.jsonData?.message || "Unable to reset password");
      }
    } catch (err) {
      setError(err?.message || "Unable to reset password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="mkt-page">
      <header className="mkt-hero">
        <h1>Reset password</h1>
        {done ? (
          <p data-testid="reset-password-success">Your password has been updated.</p>
        ) : (
          <p>Choose a new password for your account.</p>
        )}
      </header>
      <section className="mkt-section">
        {done ? (
          <CustomButton
            type="button"
            variant="contained"
            color="primary"
            onClick={() => {
              openLogin?.();
              navigate("/login");
            }}
          >
            Log in
          </CustomButton>
        ) : (
          <form className="login loginForm" onSubmit={onSubmit} data-testid="reset-password-form">
            {!token ? (
              <p className="fieldHint">This reset link is missing a token. Request a new one from the login screen.</p>
            ) : null}
            <div className="field form-group">
              <label className="form-label" htmlFor="reset-password">
                New password
              </label>
              <input
                id="reset-password"
                type="password"
                className="form-input"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div className="field form-group">
              <label className="form-label" htmlFor="reset-password-confirm">
                Confirm new password
              </label>
              <input
                id="reset-password-confirm"
                type="password"
                className="form-input"
                autoComplete="new-password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                required
              />
            </div>
            {error ? <p className="fieldHint" style={{ color: "#b00020" }}>{error}</p> : null}
            <CustomButton type="submit" variant="contained" color="primary" disabled={busy || !token}>
              Set password
            </CustomButton>
          </form>
        )}
        <p style={{ marginTop: "1rem" }}>
          <Link to="/">Return home</Link>
        </p>
      </section>
    </article>
  );
}
